import type {
  ActivatedRouteSnapshot,
  CanDeactivateFn,
  GuardResult,
  RouterStateSnapshot,
} from '@angular/router';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { catchError, from, Observable, of } from 'rxjs';
import { concatMap, map, reduce, takeWhile } from 'rxjs/operators';

/**
 * A utility function that sequentially executes an array of `CanDeactivateFn` guards and combines their results.
 *
 * The function processes the guards one at a time in the order they are defined within the provided array.
 * If all guards return `true`, the overall result will also be `true`. If any guard returns `false`, a non-boolean value (e.g., `UrlTree`),
 * or throws an error, the execution halts, and the appropriate result is returned.
 *
 * Guards can return the following types of results which are handled by this function:
 * - `boolean`
 * - `Promise<boolean | UrlTree>`
 * - `Observable<boolean | UrlTree>`
 *
 * The function ensures that the execution respects Angular's dependency injection context for each guard by using `runInInjectionContext`.
 *
 * If an error occurs during guard execution, it is caught, logged to the console, and a result of `false` is returned.
 *
 * @param {CanDeactivateFn<any>[]} guards - An array of `CanDeactivateFn` guards to be executed sequentially.
 *
 * @returns {CanDeactivateFn<any>} A composite `CanDeactivateFn` that executes the guards sequentially and resolves their combined result.
 */
export const sequentialDeactivateGuards: (guards: CanDeactivateFn<any>[]) => CanDeactivateFn<any> =
  <T>(guards: CanDeactivateFn<any>[]): CanDeactivateFn<any> =>
  (
    component: T,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState: RouterStateSnapshot,
  ): Observable<GuardResult> => {
    if (!Array.isArray(guards) || guards.length === 0) {
      return of(true);
    }

    const injector = inject(Injector);

    return from(guards).pipe(
      concatMap(guard => {
        try {
          const result = runInInjectionContext(injector, () =>
            guard(component, currentRoute, currentState, nextState),
          );

          // Konvertáljuk Observable-ra ha szükséges
          if (result instanceof Observable) {
            return result;
          } else if (result instanceof Promise) {
            return from(result);
          }
          return of(result);
        } catch (error) {
          console.error('Error executing guard:', error);
          return of(false);
        }
      }),
      // Csak addig folytatjuk, amíg az eredmény true (boolean)
      takeWhile((result: GuardResult) => result === true, true), // inclusive true = az utolsó false értéket is befoglaljuk
      // Összegyűjtjük az eredményeket
      reduce((acc: GuardResult[], current: GuardResult) => {
        acc.push(current);
        return acc;
      }, []),
      // Ellenőrizzük, hogy minden eredmény true-e és visszaadjuk a megfelelő GuardResult-ot
      map((results: GuardResult[]) => {
        const allTrue = results.every(result => result === true);
        if (allTrue) {
          return true;
        }
        // Ha van non-boolean eredmény (pl. UrlTree), azt adjuk vissza
        const nonBooleanResult = results.find(result => typeof result !== 'boolean');
        return nonBooleanResult || false;
      }),
      catchError(error => {
        console.error('Unexpected error in guard execution:', error);
        return of(false);
      }),
    );
  };
